import { useCallback, useEffect, useState } from "react";

// Chrome/Edge fire `beforeinstallprompt` when the PWA is installable (valid
// manifest + SW, not already installed). We capture it so we can show our own
// prompt and trigger the native install on tap. iOS Safari never fires this —
// that path is handled by the install coach.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function useInstallPrompt(): {
  canInstall: boolean;
  promptInstall: () => void;
} {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault(); // stop Chrome's own mini-infobar; we show our card
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setDeferred(null);
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(() => {
    if (!deferred) return;
    // The event is single-use; drop it now so a stale prompt can't be re-fired
    // and the card stops showing the install button regardless of outcome.
    setDeferred(null);
    deferred.prompt().catch(() => {}); // stale/duplicate prompt() rejects — nothing to recover
  }, [deferred]);

  return { canInstall: !!deferred, promptInstall };
}
